/* eslint-disable import/no-extraneous-dependencies */

import { GridColDef } from '@mui/x-data-grid';
import { BrowseView, browseViewColumnAction, getMessageFromError } from '@pro4-erp/core-web';
import { useNavigate } from 'react-router-dom';
import { Check as CheckIcon, Edit as EditIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { ClientWorkOrder } from '../types';
import { getClientWorkOrdersForMe } from '../api/getClientWorkOrdersForMe';
import { selectUserWorkOrder } from '../api/selectUserWorkOrder';
import { useAuthStore, useUpdateUserToken } from '@/core';

const BrowsePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authStore = useAuthStore();
  const { enqueueSnackbar } = useSnackbar();
  const updateToken = useUpdateUserToken();
  const { mutate } = useMutation({
    mutationFn: selectUserWorkOrder,
    onSuccess: async () => {
      enqueueSnackbar(
        t('Successfully saved changes.', { defaultValue: 'Successfully saved changes.' }),
        { variant: 'success' }
      );
      updateToken();
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });
  const columns: GridColDef<ClientWorkOrder>[] = [
    {
      ...browseViewColumnAction<ClientWorkOrder>({
        field: 'Choose',
        headerName: t('Choose', { defaultValue: 'Choose' }).toString(),
        icon: {
          element: <CheckIcon />,
          color: 'primary',
        },
        onClick: ({ params }) => {
          if (authStore.user?.osv.WOId !== params.row.id) mutate(params.row.id);
        },
      }),
      display: 'flex',
    },
    {
      ...browseViewColumnAction({
        field: 'actions',
        headerName: t('Open', { defaultValue: 'Open' }),
        icon: {
          element: <EditIcon />,
          color: 'primary',
        },
        onClick: ({ params }) => navigate(`/client-work-order/${params.id}`),
      }),
      display: 'flex',
    },

    {
      field: 'title',
      display: 'flex',
      headerName: t('Organization title', { defaultValue: 'Organization title' }).toString(),
      flex: 1.5,
      cellClassName: ({ row }) => {
        if (authStore.user?.osv.WOId === row.id) return 'selected';
        return '';
      },
      minWidth: 130,
      disableColumnMenu: true,
      sortable: false,
      valueGetter(_, row) {
        return row.title || 'N/A';
      },
    },
    {
      field: 'address',
      display: 'flex',
      headerName: t('Address', { defaultValue: 'Address' }).toString(),
      flex: 1.5,
      minWidth: 130,
      disableColumnMenu: true,
      cellClassName: ({ row }) => {
        if (authStore.user?.osv.WOId === row.id) return 'selected';
        return '';
      },
      sortable: false,
      valueGetter(_, row) {
        return row.address || 'N/A';
      },
    },
    {
      field: 'postOffice',
      display: 'flex',
      headerName: t('Post office', { defaultValue: 'Post office' }).toString(),
      flex: 1.5,
      minWidth: 130,
      disableColumnMenu: true,
      cellClassName: ({ row }) => {
        if (authStore.user?.osv.WOId === row.id) return 'selected';
        return '';
      },
      sortable: false,
      valueGetter(_, row) {
        return row.postOffice || 'N/A';
      },
    },
    {
      field: 'employees',
      display: 'flex',
      headerName: t('Num of employees', { defaultValue: 'Num of employees' }).toString(),
      flex: 1.5,
      minWidth: 130,
      disableColumnMenu: true,
      cellClassName: ({ row }) => {
        if (authStore.user?.osv.WOId === row.id) return 'selected';
        return '';
      },
      sortable: false,
      valueGetter(_, row) {
        return row.employees || 0;
      },
    },
  ];

  return (
    <BrowseView
      title={
        <BrowseView.Title>
          {t('Organization data', { defaultValue: 'Organization data' })}
        </BrowseView.Title>
      }
      browserViewContainerSx={{
        height: '100vh',
        '& .MuiDataGrid-cell.selected': {
          bgcolor: '#e3f2fd',
        },
      }}
      columns={columns}
      fetchData={getClientWorkOrdersForMe}
      toolbar={<BrowseView.Toolbar showSearch />}
      getRowHeight={() => 'auto'}
    />
  );
};

export default BrowsePage;
